/**
 * Vista: Distribuidores por corregir (ERP F1, B2 · D5)
 * Entregas, órdenes de trabajo y unidades cuyo «distribuidor» NO es un distribuidor válido
 * (hoy: 5 entregas y 2 OT que dicen JESUS EMANUEL BELLO RENDON, que es empleado).
 * El sistema NO adivina quién fue el distribuidor real: Jorge o Mario lo eligen aquí, renglón por renglón.
 * La corrección cambia sólo el distribuidor (no reabre el documento) y queda en la bitácora.
 */

import personasErp from '../../services/erpPersonas.service.js';
import permisos from '../../services/permissions.service.js';
import modal from '../../utils/modal.js';
import toast from '../../utils/toast.js';
import { esc, errMsg, badge } from './erpUi.js';
import { pestanasHtml, Pestanas } from '../../utils/pestanasErp.js';

const DOC = { entrega: 'Entrega', orden_trabajo: 'Orden de trabajo', tractor: 'Unidad' };
const RUTA = { entrega: (r) => `/ventas/entregas/${r.documento_id}`, orden_trabajo: (r) => `/produccion/ordenes-trabajo/${r.documento_id}`, tractor: () => '/tractores' };
const PROBLEMA = { es_empleado: ['es empleado', 'danger'], sin_rol_distribuidor: ['no es distribuidor', 'warning'], no_existe: ['no existe', 'danger'] };

const fmtFecha = (f) => {
  if (!f) return '—';
  const s = String(f).slice(0, 10).split('-');
  return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : esc(f);
};

export default {
  data: { pendientes: [], distribuidores: [] },

  async render() {
    // Patrón de 2 pestañas, sólo con «Consultar»: aquí no se da de alta nada. La única acción (elegir el distribuidor
    // real y «Corregir») va en el mismo renglón, junto a la serie, el cliente y lo que dice hoy: llevarla a otra pestaña
    // separaría la decisión de los datos que la justifican. Sin permiso de editar, el renglón sólo se consulta.
    const consultar = `
      <div class="erp-aviso erp-aviso-warn">
        Estos documentos tienen como <strong>distribuidor</strong> a alguien que no lo es (por ejemplo, un empleado).
        <strong>El sistema no adivina</strong> quién fue el distribuidor real: elígelo en cada renglón.
        Si la venta la hizo AgroMX directamente, elige <strong>Venta directa AgroMX</strong>.
        Sólo cambia el distribuidor; el documento no se reabre y el cambio queda en la bitácora.
      </div>
      <div class="card">
        <div class="card-header"><h2 class="card-title">Pendientes <span id="cd_n" class="text-muted"></span></h2></div>
        <div class="table-container">
          <table class="table">
            <thead><tr><th>Documento</th><th>Serie</th><th class="tbl-hide-mobile">Fecha</th><th class="tbl-hide-mobile">Cliente</th><th>Dice hoy</th><th>Distribuidor real</th><th></th></tr></thead>
            <tbody id="cdBody"><tr><td colspan="7" class="text-center"><div class="loading-spinner"></div></td></tr></tbody>
          </table>
        </div>
      </div>
    `;
    return pestanasHtml({ id: 'cd', titulo: 'Distribuidores por corregir', puedeCrear: false, conFormulario: false, consultar });
  },

  async afterRender() {
    if (!permisos.canView('calidad_distribuidor')) {
      document.getElementById('content').innerHTML = '<div style="padding:2rem;text-align:center;"><h2>Acceso denegado</h2></div>';
      return;
    }
    window.currentView = this;
    this.tabs = new Pestanas('cd');
    await this.cargar();
  },

  async cargar() {
    try {
      const [rc, rd] = await Promise.all([personasErp.calidad(), personasErp.distribuidores()]);
      this.data.pendientes = rc?.data?.pendientes || [];
      this.data.distribuidores = rd?.data?.personas || [];
      this.renderLista();
    } catch (err) {
      document.getElementById('cdBody').innerHTML = `<tr><td colspan="7" class="text-center text-muted">${esc(errMsg(err, 'No se pudo cargar'))}</td></tr>`;
    }
  },

  renderLista() {
    const body = document.getElementById('cdBody');
    const n = document.getElementById('cd_n');
    const lista = this.data.pendientes;
    if (n) n.textContent = lista.length ? `(${lista.length})` : '';
    if (!lista.length) {
      body.innerHTML = '<tr><td colspan="7" class="text-center" style="padding:1.5rem;">✅ No hay nada por corregir: todos los distribuidores son válidos.</td></tr>';
      return;
    }
    const puede = permisos.canEdit('calidad_distribuidor');
    const opciones = '<option value="">— Elige —</option>' + this.data.distribuidores
      .map((d) => `<option value="${d.id}">${esc(d.nombre_completo)}${d.venta_directa ? ' (AgroMX)' : ''}</option>`).join('');
    body.innerHTML = lista.map((r, i) => {
      const [txt, tipo] = PROBLEMA[r.problema] || [r.problema, 'secondary'];
      const etiqueta = r.documento === 'tractor' ? DOC.tractor : `${DOC[r.documento]} #${r.documento_id}`;
      return `
      <tr>
        <td><a href="${RUTA[r.documento](r)}" data-link><strong>${esc(etiqueta)}</strong></a>
          ${r.cerrado ? '<div style="font-size:.72rem;" class="text-muted">cerrado</div>' : ''}</td>
        <td><code style="font-size:.75rem;">${esc(r.num_serie)}</code></td>
        <td class="tbl-hide-mobile">${fmtFecha(r.fecha)}</td>
        <td class="tbl-hide-mobile">${r.cliente_nombre ? esc(r.cliente_nombre) : '<span class="text-muted">—</span>'}</td>
        <td>${r.persona_nombre ? esc(r.persona_nombre) : '<span class="text-muted">—</span>'} ${r.persona_codigo ? `<code style="font-size:.7rem;">${esc(r.persona_codigo)}</code>` : ''}
          <div>${badge(txt, tipo)}</div></td>
        <td>${puede ? `<select class="form-control" id="cd_sel_${i}" style="min-width:200px;">${opciones}</select>` : '<span class="text-muted">sin permiso</span>'}</td>
        <td style="text-align:right;">${puede ? `<button class="btn btn-primary btn-sm" onclick="window.currentView.corregir(${i})">Corregir</button>` : ''}</td>
      </tr>`;
    }).join('');
  },

  corregir(i) {
    const r = this.data.pendientes[i];
    const sel = document.getElementById(`cd_sel_${i}`);
    const id = sel?.value;
    if (!id) { toast.warning('Elige al distribuidor real'); return; }
    const d = this.data.distribuidores.find((x) => String(x.id) === String(id));
    const etiqueta = r.documento === 'tractor' ? `la unidad ${r.num_serie}` : `${DOC[r.documento].toLowerCase()} #${r.documento_id} (${r.num_serie})`;
    modal.confirm('Corregir distribuidor', `
      <p>En ${esc(etiqueta)} el distribuidor pasa de <strong>${esc(r.persona_nombre || '—')}</strong> a <strong>${esc(d.nombre_completo)}</strong>.</p>
      <div class="form-group"><label>Nota (opcional)</label><input type="text" id="cd_nota" class="form-control" maxlength="250" placeholder="Ej: confirmado con Mario el 20-oct" /></div>`,
    async () => {
      const nota = document.getElementById('cd_nota')?.value?.trim() || null;
      const docId = r.documento === 'tractor' ? r.num_serie : r.documento_id;
      try {
        await personasErp.corregir(r.documento, docId, { distribuidor_persona_id: Number(id), nota });
        toast.success(`Corregido: ${d.nombre_completo}`);
        await this.cargar();
      } catch (err) { toast.error(errMsg(err)); }
    });
  }
};
